import { useId, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Loader2,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Building,
  Globe,
  Check,
  ChevronsUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { TIMEZONES, formatTimezoneLabel } from '@/lib/timezone-options';
import { PhoneInput } from '@/components/ui/phone-input';
import { authService } from '../services/auth.service';
import { toast } from 'sonner';
import type { RegisterResponse } from '../types/auth.types';

// Validation Schema matching backend TenantSignupDto

const registerSchema = z
  .object({
    // Company Information
    companyName: z
      .string()
      .min(1, 'Company name is required')
      .min(2, 'Company name must be at least 2 characters')
      .max(100, 'Company name must not exceed 100 characters')
      .regex(
        /^[a-zA-Z0-9\s&\-'.]+$/,
        "Company name can only contain letters, numbers, spaces, and & - ' .",
      ),
    companyEmail: z
      .string()
      .min(1, 'Company email is required')
      .email('Please enter a valid company email address'),
    adminPhone: z
      .string()
      .min(1, 'Phone number is required for verification')
      .regex(/^\+[1-9]\d{6,14}$/, 'Phone must be in E.164 format (e.g., +919876543210)'),
    // Admin Information
    adminFirstName: z
      .string()
      .min(1, 'First name is required')
      .max(50, 'First name must not exceed 50 characters')
      .regex(
        /^[a-zA-Z\s\-']+$/,
        'First name can only contain letters, spaces, hyphens, and apostrophes',
      ),
    adminLastName: z
      .string()
      .min(1, 'Last name is required')
      .max(50, 'Last name must not exceed 50 characters')
      .regex(
        /^[a-zA-Z\s\-']+$/,
        'Last name can only contain letters, spaces, hyphens, and apostrophes',
      ),
    adminEmail: z
      .string()
      .min(1, 'Admin email is required')
      .email('Please enter a valid admin email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        'Password must contain at least one special character',
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    // Legal Consent
    acceptedTerms: z.boolean().refine((val) => val === true, {
      message: 'You must accept the terms and conditions',
    }),
    // Timezone (defaults to Asia/Kolkata for Indian tenants)
    timezone: z.string().default('Asia/Kolkata'),
    acceptedPrivacy: z.boolean().refine((val) => val === true, {
      message: 'You must accept the privacy policy',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterFormInput = z.input<typeof registerSchema>;
type RegisterFormData = z.output<typeof registerSchema>;

function getRegisterErrorMessage(error: unknown): string | string[] {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return 'Registration failed. Please try again.';
  }

  const response = error.response as { data?: { message?: unknown } } | undefined;
  const message = response?.data?.message;

  if (typeof message === 'string') {
    return message;
  }

  if (Array.isArray(message) && message.every((item) => typeof item === 'string')) {
    return message;
  }

  return 'Registration failed. Please try again.';
}

export function RegisterForm() {
  const navigate = useNavigate();
  const timezoneLabelId = useId();
  const timezoneSelectedId = useId();
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<RegisterFormInput, unknown, RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      companyName: '',
      companyEmail: '',
      adminPhone: '',
      adminFirstName: '',
      adminLastName: '',
      adminEmail: '',
      password: '',
      confirmPassword: '',
      timezone: 'Asia/Kolkata',
      acceptedTerms: false,
      acceptedPrivacy: false,
    },
  });

  const password = useWatch({ control, name: 'password' });
  const acceptedTerms = useWatch({ control, name: 'acceptedTerms' });
  const acceptedPrivacy = useWatch({ control, name: 'acceptedPrivacy' });

  const registerMutation = useMutation({
    mutationFn: authService.register,
    onSuccess: (data: RegisterResponse) => {
      const existingLogin = data.login;

      if (data.nextAction === 'SIGN_IN' && existingLogin) {
        toast.info('Account Already Exists', {
          description: 'Sign in to continue verification.',
        });
        navigate({
          to: '/login',
          search: {
            email: existingLogin.email,
            tenantSlug: existingLogin.tenantSlug,
            verify: true,
          },
        });
        return;
      }

      if (!data.nextSteps) {
        toast.error('Registration Failed', {
          description: 'Signup response did not include verification steps.',
        });
        return;
      }

      toast.success('Account Created!', {
        description: data.nextSteps.verifyPhone
          ? 'Please verify your company email, admin email, and phone. Company setup comes next before employees are added.'
          : 'Please verify your company email and admin email. Company setup comes next before employees are added.',
      });

      navigate({
        to: '/login',
        search: {
          email: data.admin.email,
          tenantSlug: data.tenant.slug,
          verify: true,
        },
      });
    },
    onError: (error: unknown) => {
      const message = getRegisterErrorMessage(error);
      toast.error('Registration Failed', {
        description: Array.isArray(message) ? message.join(', ') : message,
      });
    },
  });

  const onSubmit = (data: RegisterFormData) => {
    const registerData = {
      companyName: data.companyName,
      companyEmail: data.companyEmail,
      adminPhone: data.adminPhone,
      adminFirstName: data.adminFirstName,
      adminLastName: data.adminLastName,
      adminEmail: data.adminEmail,
      password: data.password,
      acceptedTerms: data.acceptedTerms,
      timezone: data.timezone,
      acceptedPrivacy: data.acceptedPrivacy,
    };
    registerMutation.mutate(registerData);
  };

  // Password strength indicator
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength === 3) return { strength, label: 'Fair', color: 'bg-yellow-500' };
    if (strength === 4) return { strength, label: 'Good', color: 'bg-blue-500' };
    return { strength, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <Card className="w-full max-w-2xl shadow-2xl my-8">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-400 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white text-2xl font-bold">U</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Create your tenant admin account</CardTitle>
          <CardDescription>
            Start with an admin identity. Company setup and employee codes come after verification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Tenant Information */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium">Tenant Information</p>

              <div className="space-y-2">
                <Label htmlFor="companyName">Tenant / Group Name *</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="companyName"
                    placeholder="Acme Corporation"
                    className="pl-10"
                    {...register('companyName')}
                    disabled={registerMutation.isPending}
                  />
                </div>
                {errors.companyName && (
                  <p className="text-sm text-destructive">{errors.companyName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyEmail">Tenant Contact Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="companyEmail"
                    type="email"
                    placeholder="contact@company.com"
                    className="pl-10"
                    {...register('companyEmail')}
                    disabled={registerMutation.isPending}
                  />
                </div>
                {errors.companyEmail && (
                  <p className="text-sm text-destructive">{errors.companyEmail.message}</p>
                )}
              </div>
            </div>

            {/* Timezone Selection */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium">Regional Settings</p>
              <div className="space-y-2">
                <Label id={timezoneLabelId}>Timezone</Label>
                <Controller
                  name="timezone"
                  control={control}
                  render={({ field }) => {
                    const selected = TIMEZONES.find((tz) => tz.value === field.value);
                    return (
                      <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={timezoneOpen}
                            aria-labelledby={`${timezoneLabelId} ${timezoneSelectedId}`}
                            className="w-full justify-between font-normal"
                            disabled={registerMutation.isPending}
                            name={field.name}
                            onBlur={field.onBlur}
                            ref={field.ref}
                          >
                            <div className="flex min-w-0 items-center gap-2 text-left">
                              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span id={timezoneSelectedId} className="truncate">
                                {selected ? formatTimezoneLabel(selected) : 'Select timezone...'}
                              </span>
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search IANA timezone, city, or offset..." />
                            <CommandList>
                              <CommandEmpty>No timezone found.</CommandEmpty>
                              <CommandGroup>
                                {TIMEZONES.map((tz) => (
                                  <CommandItem
                                    key={tz.value}
                                    value={`${tz.label} ${tz.value}`}
                                    className="items-start gap-2"
                                    onSelect={() => {
                                      field.onChange(tz.value);
                                      setTimezoneOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mt-0.5 h-4 w-4 shrink-0',
                                        field.value === tz.value ? 'opacity-100' : 'opacity-0',
                                      )}
                                    />
                                    <span className="flex min-w-0 flex-col">
                                      <span className="truncate font-medium">{tz.value}</span>
                                      <span className="truncate text-xs text-muted-foreground">
                                        {tz.label}
                                      </span>
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    );
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Used for attendance, leave, and payroll calculations
                </p>
              </div>
            </div>

            {/* Admin Information */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium">First Admin Identity</p>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adminFirstName">First Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="adminFirstName"
                      placeholder="John"
                      className="pl-10"
                      {...register('adminFirstName')}
                      disabled={registerMutation.isPending}
                    />
                  </div>
                  {errors.adminFirstName && (
                    <p className="text-sm text-destructive">{errors.adminFirstName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminLastName">Last Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="adminLastName"
                      placeholder="Doe"
                      className="pl-10"
                      {...register('adminLastName')}
                      disabled={registerMutation.isPending}
                    />
                  </div>
                  {errors.adminLastName && (
                    <p className="text-sm text-destructive">{errors.adminLastName.message}</p>
                  )}
                </div>
              </div>

              {/* Admin Email & Phone */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adminEmail">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="adminEmail"
                      type="email"
                      placeholder="admin@company.com"
                      className="pl-10"
                      {...register('adminEmail')}
                      disabled={registerMutation.isPending}
                    />
                  </div>
                  {errors.adminEmail && (
                    <p className="text-sm text-destructive">{errors.adminEmail.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPhone">Mobile *</Label>
                  <Controller
                    name="adminPhone"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        value={field.value}
                        onChange={field.onChange}
                        defaultCountry="in"
                        placeholder="9876543210"
                        disabled={registerMutation.isPending}
                      />
                    )}
                  />
                  <p className="text-xs text-muted-foreground">
                    Used for verification only; this does not create an employee profile
                  </p>
                  {errors.adminPhone && (
                    <p className="text-sm text-destructive">{errors.adminPhone.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="********"
                  className="pl-10 pr-10"
                  {...register('password')}
                  disabled={registerMutation.isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {password && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < passwordStrength.strength
                            ? passwordStrength.color
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password strength: <span className="font-medium">{passwordStrength.label}</span>
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="********"
                  className="pl-10 pr-10"
                  {...register('confirmPassword')}
                  disabled={registerMutation.isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
              )}
            </div>

            {/* Terms and Privacy */}
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptedTerms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setValue('acceptedTerms', !!checked)}
                  disabled={registerMutation.isPending}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="acceptedTerms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    I accept the{' '}
                    <a href="#" className="text-blue-600 hover:underline">
                      Terms and Conditions
                    </a>{' '}
                    *
                  </label>
                </div>
              </div>
              {errors.acceptedTerms && (
                <p className="text-sm text-destructive">{errors.acceptedTerms.message}</p>
              )}

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="acceptedPrivacy"
                  checked={acceptedPrivacy}
                  onCheckedChange={(checked) => setValue('acceptedPrivacy', !!checked)}
                  disabled={registerMutation.isPending}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="acceptedPrivacy"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    I accept the{' '}
                    <a href="#" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </a>{' '}
                    *
                  </label>
                </div>
              </div>
              {errors.acceptedPrivacy && (
                <p className="text-sm text-destructive">{errors.acceptedPrivacy.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
            >
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
